/**
 * Meta's delivery-shape value types: what a campaign optimizes for, where it sends people,
 * and how conversions are attributed back to it.
 *
 * These live here rather than alongside the rest of the Facebook API types because the ad
 * builder's optimization catalog is keyed by them and the builder consumes it on every step.
 * `@marketing-milk/backend` re-exports them, so importers there are unaffected.
 */

export enum OutcomeCampaignObjective {
  OUTCOME_APP_PROMOTION = 'OUTCOME_APP_PROMOTION',
  OUTCOME_AWARENESS = 'OUTCOME_AWARENESS',
  OUTCOME_ENGAGEMENT = 'OUTCOME_ENGAGEMENT',
  OUTCOME_LEADS = 'OUTCOME_LEADS',
  OUTCOME_SALES = 'OUTCOME_SALES',
  OUTCOME_TRAFFIC = 'OUTCOME_TRAFFIC',
}

export enum OptimizationGoal {
  NONE = 'NONE',
  APP_INSTALLS = 'APP_INSTALLS',
  BRAND_AWARENESS = 'BRAND_AWARENESS',
  AD_RECALL_LIFT = 'AD_RECALL_LIFT',
  CLICKS = 'CLICKS',
  ENGAGED_USERS = 'ENGAGED_USERS',
  EVENT_RESPONSES = 'EVENT_RESPONSES',
  IMPRESSIONS = 'IMPRESSIONS',
  LEAD_GENERATION = 'LEAD_GENERATION',
  LINK_CLICKS = 'LINK_CLICKS',
  OFFER_CLAIMS = 'OFFER_CLAIMS',
  OFFSITE_CONVERSIONS = 'OFFSITE_CONVERSIONS',
  PAGE_ENGAGEMENT = 'PAGE_ENGAGEMENT',
  PAGE_LIKES = 'PAGE_LIKES',
  POST_ENGAGEMENT = 'POST_ENGAGEMENT',
  REACH = 'REACH',
  SOCIAL_IMPRESSIONS = 'SOCIAL_IMPRESSIONS',
  VIDEO_VIEWS = 'VIDEO_VIEWS',
  APP_DOWNLOADS = 'APP_DOWNLOADS',
  TWO_SECOND_CONTINUOUS_VIDEO_VIEWS = 'TWO_SECOND_CONTINUOUS_VIDEO_VIEWS',
  LANDING_PAGE_VIEWS = 'LANDING_PAGE_VIEWS',
  VALUE = 'VALUE',
  THRUPLAY = 'THRUPLAY',
  REPLIES = 'REPLIES',
  DERIVED_EVENTS = 'DERIVED_EVENTS',
}

export enum DestinationType {
  UNDEFINED = 'UNDEFINED',
  ON_AD = 'ON_AD',
  WEBSITE = 'WEBSITE',
  /**
   * Leads · "Website and instant forms": Meta routes each person to the site or the form on the ad.
   * Absent from Meta's docs and SDK; read back from an ad set built in Ads Manager.
   */
  WEBSITE_AND_LEAD_FORM = 'WEBSITE_AND_LEAD_FORM',
  APP = 'APP',
  MESSENGER = 'MESSENGER',
  APPLINKS_AUTOMATIC = 'APPLINKS_AUTOMATIC',
  FACEBOOK = 'FACEBOOK',
}

export type AttributionDays = 1 | 7 | 28;

export enum AttributionEventType {
  CLICK_THROUGH = 'CLICK_THROUGH',
  VIEW_THROUGH = 'VIEW_THROUGH',
}

export interface AttributionSpec {
  event_type: AttributionEventType;
  window_days: AttributionDays;
}

export enum FrequencyControlEvent {
  IMPRESSIONS = 'IMPRESSIONS', // only one supported currently
  VIDEO_VIEWS = 'VIDEO_VIEWS',
  VIDEO_VIEWS_2S = 'VIDEO_VIEWS_2S',
  VIDEO_VIEWS_15S = 'VIDEO_VIEWS_15S',
}

export interface FrequencyControlSpec {
  event: FrequencyControlEvent;
  interval_days: number;
  max_frequency: number;
}
